import type { NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Head from 'next/head';
import styled from '@emotion/styled';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';

// components
import DefaultLayout from '@components/_templates/DefaultLayout';
import Title from '@components/_atoms/Title';
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';
import CardSelect from '@components/_atoms/CardSelect';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';

// constants
import ButtonCode from '@constants/log/buttonCode';
import { MOBILE_MODE, TABLET_MODE } from '@constants/size';
import { ButtonColor, CardSelectSize, TypoVariant } from '@constants/atoms';
import { BUYER_SELECT_TYPE, BUYER_TYPE, LOGIN } from '@constants/routes/routes';
import { loginStatusState, previousPageUrlState } from '@recoil/auth';

import { gray } from '@styles/Colors';

const ServiceWrapper = styled.div`
  width: 100%;
  max-width: 528px;
  padding: 110px 0;
  margin: 0 auto;

  @media ${TABLET_MODE} {
    max-width: 768px;
    padding: 110px 40px;
  }

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 104px 0;
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: 8px;
`;

const SubTitleWrapper = styled.div`
  margin-bottom: 80px;
  text-align: center;

  @media ${MOBILE_MODE} {
    margin-bottom: 40px;
  }
`;

const CardSelectWrapper = styled.div`
  margin-bottom: 80px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;

  @media ${MOBILE_MODE} {
    margin-bottom: 100px;

    grid-template-columns: none;
    row-gap: 16px;
  }
`;

const Buyer: NextPage = () => {
  const { t } = useTranslation('service');
  const router = useRouter();
  const isMobile = useIsMobile();

  const [loginStatus] = useRecoilState(loginStatusState);
  const setPreviousPageUrl = useSetRecoilState(previousPageUrlState);

  const handleClickUploadFile = () => {
    if (!loginStatus) {
      setPreviousPageUrl(BUYER_TYPE);
      router.push(LOGIN);
      return;
    }

    router.push(BUYER_TYPE);
  };

  return (
    <>
      <Head>
        <title>{t('buyer.meta-title')}</title>
        <meta name="title" content={t('buyer.meta-title')} />
      </Head>
      <DefaultLayout>
        <ServiceWrapper>
          <TitleWrapper>
            <Title content={t('buyer.title')} />
          </TitleWrapper>
          <SubTitleWrapper>
            <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B4} color={gray.gray7}>
              {t('buyer.subTitle')}
            </Typography>
          </SubTitleWrapper>
          <CardSelectWrapper>
            {/* 검색하기 */}
            <Link href={BUYER_SELECT_TYPE}>
              <a data-steelboso-id={ButtonCode.BTN_BUY_SEARCH}>
                <CardSelect
                  iconName="search-things"
                  height="100%"
                  title={t('buyer.card1-title')}
                  subTitle={t('buyer.card1-subTitle')}
                  size={isMobile ? CardSelectSize.SMALL : CardSelectSize.MEDIUM}
                />
              </a>
            </Link>
            {/* 파일 등록하기 */}
            <a data-steelboso-id={ButtonCode.BTN_WRITE}>
              <CardSelect
                iconName="upload-file"
                height="100%"
                title={t('buyer.card2-title')}
                subTitle={t('buyer.card2-subTitle')}
                size={isMobile ? CardSelectSize.SMALL : CardSelectSize.MEDIUM}
                onClick={handleClickUploadFile}
              />
            </a>
          </CardSelectWrapper>

          <DefaultButtonBox
            button1={
              <Button
                color={ButtonColor.OutlinedGray}
                text={t('buyer.back-button')}
                onClick={() => router.back()}
                steelbosoId={ButtonCode.BTN_BACK}
              />
            }
          />
        </ServiceWrapper>
      </DefaultLayout>
    </>
  );
};

export const getStaticProps = async ({ locale }: { locale: any }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['service', 'common', 'component'])),
  },
});

export default Buyer;
